import axios from 'axios';
import { fetch } from './api-wrapper';
import LRUCacheUtils from './lru-cache-utils';

/**
 * Enhanced API wrapper with LRU caching capabilities
 */
class CachedApiWrapper {
  isRequestCanceled(error) {
    // axios v0.x: axios.isCancel(error)
    // axios v1.x: error.code === 'ERR_CANCELED' / error.name === 'CanceledError'
    return (
      axios.isCancel?.(error) ||
      error?.code === 'ERR_CANCELED' ||
      error?.name === 'CanceledError'
    );
  }

  constructor() {
    // In-flight request dedup map — see `cachedFetch` for semantics.
    this.pendingRequests = new Map();
    // Create different caches for different types of data
    this.caches = {
      // General API responses
      api: LRUCacheUtils.createApiCache({
        maxSize: 20 * 1024 * 1024, // 20 MB
        ttl: 1000 * 60 * 120, // 2 hours
      }),
      // Table data with pagination
      table: LRUCacheUtils.createApiCache({
        maxSize: 15 * 1024 * 1024, // 15 MB
        ttl: 1000 * 60 * 10, // 10 minutes
      }),
      // Autocomplete/suggestions
      autocomplete: LRUCacheUtils.createAutocompleteCache({
        maxSize: 5 * 1024 * 1024, // 5 MB
        ttl: 1000 * 60 * 5, // 5 minutes
      }),
      // Static reference data
      reference: LRUCacheUtils.createApiCache({
        maxSize: 10 * 1024 * 1024, // 10 MB
        ttl: 1000 * 60 * 60, // 1 hour
      }),
      // PDF cache: now only stores lightweight metadata (e.g. signed URLs), not raw bytes
      pdf: LRUCacheUtils.createApiCache({
        maxSize: 5 * 1024 * 1024, // 5 MB is plenty for URL metadata
        ttl: 1000 * 60 * 15, // 15 minutes
      }),
    };
  }

  /**
   * Generate cache key from URL and parameters
   * @param {string} url - API endpoint
   * @param {Object} params - Request parameters
   * @param {string} method - HTTP method
   * @returns {string} Cache key
   */
  generateCacheKey(url, params = {}, method = 'GET') {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});
    
    return `${method}:${url}:${JSON.stringify(sortedParams)}`;
  }

  /**
   * Determine cache type based on URL pattern
   * @param {string} url - API endpoint
   * @returns {string} Cache type
   */
  getCacheType(url) {
    // Check for PDF URLs first (before other patterns)
    if (url.includes('/invoice/pdf/') || url.includes('/pdf/') || url.endsWith('.pdf')) {
      return 'pdf';
    }
    if (url.includes('/autocomplete') || url.includes('/search') || url.includes('?text=')) {
      return 'autocomplete';
    }
    if (url.includes('/dataset/') || url.includes('/list/') || url.includes('?start=')) {
      return 'table';
    }
    if (url.includes('/rates/') || url.includes('/imputations/') || url.includes('/reference/')) {
      return 'reference';
    }
    return 'api';
  }

  /**
   * Check if request should be cached
   * @param {string} method - HTTP method
   * @param {string} url - API endpoint
   * @returns {boolean} Whether to cache this request
   */
  shouldCache(method, url) {
    // Only cache GET requests
    if (method !== 'GET') return false;
    
    // Don't cache certain endpoints
    const noCachePatterns = [
      '/auth/',
      '/login',
      '/logout',
      '/refresh',
      '/upload',
      '/download',
    ];
    
    return !noCachePatterns.some(pattern => url.includes(pattern));
  }

  /**
   * Cached fetch method
   * @param {string} url - API endpoint
   * @param {string} method - HTTP method
   * @param {Object} params - Request parameters
   * @param {Function} callback - Success callback
   * @param {Function} errorCallback - Error callback
   * @param {boolean} isFileUploadForm - File upload flag
   * @param {Object} cancelToken - Cancel token
   * @param {Object} options - Additional options
   * @param {boolean} options.forceRefresh - Force refresh cache
   * @param {string} options.cacheType - Override cache type
   * @returns {Promise} Fetch promise
   */
  async cachedFetch(
    url, 
    method = 'GET', 
    params = {}, 
    callback = null, 
    errorCallback = null, 
    isFileUploadForm = false, 
    cancelToken = null,
    options = {}
  ) {
    // Ensure options is an object to prevent null reference errors
    const safeOptions = options || {};
    const { forceRefresh = false, cacheType = null } = safeOptions;
    
    // Check if we should cache this request
    if (!this.shouldCache(method, url)) {
      return fetch(url, method, params, callback, errorCallback, isFileUploadForm, cancelToken);
    }

    const cacheTypeToUse = cacheType || this.getCacheType(url);
    let cache = this.caches[cacheTypeToUse];
    
    // Safety check: if cache type doesn't exist, fall back to 'api' cache
    if (!cache) {
      console.warn(`Cache type '${cacheTypeToUse}' not found, falling back to 'api' cache`);
      cache = this.caches['api'];
      if (!cache) {
        // If even 'api' cache doesn't exist, something is very wrong - just fetch without cache
        return fetch(url, method, params, callback, errorCallback, isFileUploadForm, cancelToken);
      }
    }
    
    const cacheKey = this.generateCacheKey(url, params, method);

    // Check cache first (unless force refresh)
    if (!forceRefresh && cache.has(cacheKey)) {
      const cachedData = cache.get(cacheKey);
      if (callback) {
        // For all cache types (including 'pdf'), return the cached payload
        callback({ data: cachedData });
      }
      return Promise.resolve({ data: cachedData });
    }

    // No special responseType handling – backend now returns JSON (including for PDF URLs)
    const fetchOptions = {};

    // In-flight dedup: if a fetch for this exact (cacheType, cacheKey) is already
    // pending, attach our callbacks to it and return a Promise that resolves with
    // the same response. Skipped under forceRefresh so callers can force a fresh
    // network call (their request also stays off the pending map for the same
    // reason — we don't want a forceRefresh call to short-circuit a subsequent
    // non-forceRefresh joiner with stale data).
    const pendingKey = `${cacheTypeToUse}:${cacheKey}`;
    if (!forceRefresh) {
      const existing = this.pendingRequests.get(pendingKey);
      if (existing) {
        return new Promise((resolve) => {
          existing.subscribers.push({ callback, errorCallback, resolve });
        });
      }
    }

    // Make the actual API call
    return new Promise((resolve, reject) => {
      const subscribers = [{ callback, errorCallback, resolve }];
      if (!forceRefresh) {
        this.pendingRequests.set(pendingKey, { subscribers });
      }
      const clearPending = () => {
        if (!forceRefresh && this.pendingRequests.get(pendingKey)?.subscribers === subscribers) {
          this.pendingRequests.delete(pendingKey);
        }
      };
      fetch(
        url,
        method,
        params,
        (response) => {
          if (response && response.data) {
            cache.set(cacheKey, response.data);
          }
          clearPending();
          // Isolate each subscriber: a throw in caller N's callback must not
          // prevent N+1..M from receiving their callback or having their
          // promise resolved. Pre-dedup each cachedFetch was already isolated,
          // so we preserve that property here.
          subscribers.forEach(({ callback: cb, resolve: res }) => {
            if (cb) {
              try { cb(response); } catch (e) { console.error('cachedFetch subscriber callback threw:', e); }
            }
            res(response);
          });
        },
        (error) => {
          clearPending();
          // Treat deliberate cancels (unmount/navigation) as a silent no-op.
          // Many callers do not await cachedFetch(), so rejecting here can create
          // unhandled promise rejections. Resolve with null after invoking errorCallback
          // so 404 and other HTTP errors don't surface as unhandled rejections.
          if (this.isRequestCanceled(error)) {
            subscribers.forEach(({ resolve: res }) => res(null));
            return;
          }
          subscribers.forEach(({ errorCallback: ec, resolve: res }) => {
            if (ec) {
              try { ec(error); } catch (e) { console.error('cachedFetch subscriber errorCallback threw:', e); }
            }
            res(null);
          });
        },
        isFileUploadForm,
        cancelToken,
        0,
        fetchOptions
      );
    });
  }

  /**
   * Clear specific cache type
   * @param {string} cacheType - Cache type to clear
   */
  clearCache(cacheType = null) {
    if (cacheType) {
      this.caches[cacheType].clear();
    } else {
      Object.values(this.caches).forEach(cache => cache.clear());
    }
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    const stats = {};
    Object.entries(this.caches).forEach(([type, cache]) => {
      stats[type] = {
        size: cache.size,
        maxSize: cache.maxSize,
        calculatedSize: cache.calculatedSize || 0,
      };
    });
    return stats;
  }

  /**
   * Remove specific item from cache
   * @param {string} url - API endpoint
   * @param {Object} params - Request parameters
   * @param {string} method - HTTP method
   */
  removeFromCache(url, params = {}, method = 'GET') {
    const cacheType = this.getCacheType(url);
    const cache = this.caches[cacheType];
    const cacheKey = this.generateCacheKey(url, params, method);
    cache.delete(cacheKey);
  }
}

// Create singleton instance
const cachedApi = new CachedApiWrapper();

// Export the cached fetch function for easy use
export const cachedFetch = cachedApi.cachedFetch.bind(cachedApi);

// Export the instance for advanced usage
export default cachedApi;
